/** Tracking campaigns — the group a set of links belongs to. */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { useTracking, changed } from '../../lib/urltracking.js';
import { formatDate, parseDate } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, CardBody, EmptyState, Avatar, Dash } from '../../components/Primitives.jsx';
import { CAMPAIGN_STATUS } from '../../constants/urltracking.js';
import { TrackStatus, TrackState, num } from './TrackBits.jsx';

export default function TrackCampaigns() {
  const { S, setSheet, can, toast } = useApp();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const { data, error, loading } = useTracking(
    () => api.urlTracking.campaigns.list({ q, status, archived: showArchived ? 'true' : '' }),
    [q, status, showArchived]
  );

  const list = data?.data || [];

  const archive = async (e, row) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Archive "${row.name}"? Its ${row.links} tracking URL${row.links === 1 ? '' : 's'} will stop redirecting, `
      + 'but every click already recorded is kept.')) return;
    try {
      await api.urlTracking.campaigns.remove(row.id);
      toast('Campaign archived');
      changed();
    } catch (err) {
      toast(err.message);
    }
  };

  const empty = !loading && !list.length ? (
    <Card>
      <CardBody>
        <EmptyState icon="mega" tone="g-pink" title="No campaigns created yet">
          <p>
            {q || status
              ? 'Nothing matches that filter.'
              : 'A campaign groups the links that belong to one push, so their clicks add up.'}
          </p>
          {can('utm-campaigns', 'create') && !q && !status ? (
            <button type="button" className="btn primary" onClick={() => setSheet({ kind: 'trackCampaign' })}>
              <Icon name="plus" />Create campaign
            </button>
          ) : null}
        </EmptyState>
      </CardBody>
    </Card>
  ) : null;

  return (
    <>
      <div className="utfilter">
        <div className="f grow">
          <label htmlFor="utc-q">Search</label>
          <input
            id="utc-q"
            value={q}
            placeholder="Campaign name or description…"
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <div className="f">
          <label htmlFor="utc-status">Status</label>
          <select id="utc-status" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Any status</option>
            {CAMPAIGN_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button
          type="button"
          className={`btn${showArchived ? ' on' : ''}`}
          onClick={() => setShowArchived(v => !v)}
          title="Archived campaigns are hidden by default"
        >
          <Icon name={showArchived ? 'eye' : 'eyeoff'} />Archived
        </button>
      </div>

      <TrackState loading={loading && !data} error={error} empty={empty}>
        <Card>
          <CardHead icon="mega" tone="g-pink" title="Campaigns" count={list.length} />
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>Campaign</th><th>Status</th><th>URLs</th><th>Clicks</th>
                  <th>Unique</th><th>First click</th><th>Last click</th><th>Runs</th><th />
                </tr>
              </thead>
              <tbody>
                {list.map(c => (
                  <tr key={c.id} onClick={() => setSheet({ kind: 'trackCampaignView', id: c.id })}>
                    <td>
                      <div className="cname">
                        <Avatar name={c.name} />
                        <div>
                          <b>{c.name}</b>
                          <small>{c.description || 'No description'}</small>
                        </div>
                      </div>
                    </td>
                    <td><TrackStatus value={c.status} /></td>
                    <td>{num(c.links)}</td>
                    <td><b>{num(c.clicks)}</b></td>
                    <td className="dim">{num(c.uniqueVisitors)}</td>
                    <td>{stamp(c.first_click, S.dateFmt)}</td>
                    <td>{stamp(c.last_click, S.dateFmt)}</td>
                    <td className="dim">{window_(c, S.dateFmt)}</td>
                    <td>
                      <div className="acts">
                        {can('utm-campaigns', 'edit') ? (
                          <button
                            type="button"
                            title="Edit campaign"
                            onClick={e => { e.stopPropagation(); setSheet({ kind: 'trackCampaign', id: c.id }); }}
                          >
                            <Icon name="edit" />
                          </button>
                        ) : null}
                        {can('utm-campaigns', 'delete') && c.status !== 'Archived' ? (
                          <button type="button" title="Archive campaign" onClick={e => archive(e, c)}>
                            <Icon name="trash" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </TrackState>
    </>
  );
}

const stamp = (value, fmt) => {
  const d = parseDate(value);
  return d ? <span className="datep"><Icon name="cal" />{formatDate(d, false, fmt)}</span> : <Dash />;
};

/** The campaign's own window, which is not the same as when it was clicked. */
function window_(c, fmt) {
  const from = parseDate(c.start_date);
  const to = parseDate(c.end_date);
  if (!from && !to) return '—';
  const one = d => (d ? formatDate(d, false, fmt) : '…');
  return `${one(from)} → ${one(to)}`;
}
